import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { EmptyState, IconButton } from '../components/UI';
import Icon from '../components/Icon';

type Period = 'daily' | 'weekly' | 'monthly';
const PERIODS: Period[] = ['daily', 'weekly', 'monthly'];

interface Entry {
  rank: number;
  userId: string;
  username: string;
  fullName?: string;
  qualifiedCount: number;
  prizeAmount: string | null;
}

interface Standings {
  prizes: { rank: number; amount: string }[];
  entries: Entry[];
  me: { rank: number | null; qualifiedCount: number; prizeAmount: string | null } | null;
  periodEnd?: string;
}

const MEDAL_COLOR: Record<number, string> = { 1: '#fbbf24', 2: '#cbd5e1', 3: '#d97706' };

// Prefer the person's real name; only fall back to username when no name exists.
function displayName(e: { fullName?: string; username: string }): string {
  const name = (e.fullName ?? '').trim();
  return name.length > 0 ? name : e.username;
}

function initials(e: { fullName?: string; username: string }): string {
  const source = displayName(e).trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PERIOD_LABEL: Record<Period, string> = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

export default function Leaderboard() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<Period>('daily');
  const [data, setData] = useState<Record<Period, Standings | null>>({ daily: null, weekly: null, monthly: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchPeriod = useCallback(async (p: Period, isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    setError(false);
    try {
      const res = await api.get<Standings>(`/leaderboard/${p}`);
      setData(prev => ({ ...prev, [p]: res.data }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Show cached data instantly while switching tabs; only spin the first time
  // a period loads. Always silently refresh so standings stay live.
  useEffect(() => {
    if (!data[period]) fetchPeriod(period);
    else { setLoading(false); fetchPeriod(period, true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const current = data[period];
  const me = current?.me ?? null;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('actions.back')} onPress={() => navigate(-1)} />
        <div style={{ flex: 1, fontSize: 18, fontWeight: 800, color: colors.text }}>Referral Leaderboard</div>
        <IconButton icon="redo" accessibilityLabel={t('actions.retry')} onPress={() => fetchPeriod(period, true)} />
      </div>

      {/* Period tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '14px 16px 4px' }}>
        {PERIODS.map(p => {
          const active = p === period;
          return (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 999,
                border: 'none', cursor: 'pointer',
                backgroundColor: active ? colors.accent : colors.card,
                color: active ? '#020617' : colors.textMuted,
                fontSize: 13, fontWeight: 800, fontFamily: 'inherit',
                boxShadow: active ? 'none' : `inset 0 0 0 1px ${colors.cardBorder}`,
              }}
            >
              {PERIOD_LABEL[p]}
            </button>
          );
        })}
      </div>

      <div style={{ padding: 16, paddingBottom: 40 }}>
        <div style={{ fontSize: 13, lineHeight: 1.45, color: colors.textSub, marginBottom: 16 }}>
          Climb the ranks by referring friends — top referrers win prizes each period.
        </div>

        {/* My rank card */}
        {me && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            backgroundColor: colors.card, border: `2px solid ${colors.accentDark}`,
            borderRadius: 14, padding: 14, marginBottom: 18,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              backgroundColor: colors.accentSoft,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: colors.accentText }}>
                {me.rank ? `#${me.rank}` : '—'}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>Your rank</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: colors.text, marginTop: 2 }}>
                {me.rank
                  ? `${me.qualifiedCount} ${me.qualifiedCount === 1 ? 'referral' : 'referrals'}`
                  : 'Not ranked yet'}
              </div>
            </div>
            {me.prizeAmount && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name="trophy" size={12} color="#fbbf24" />
                <div style={{ fontSize: 15, fontWeight: 900, color: colors.text }}>
                  ${Number(me.prizeAmount).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Prize pool summary */}
        {current && current.prizes.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: colors.text, marginBottom: 10 }}>Prizes this period</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {current.prizes.map(pr => (
                <div key={pr.rank} style={{
                  borderRadius: 12, border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: colors.card, padding: '8px 12px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: MEDAL_COLOR[pr.rank] ?? colors.textMuted }}>
                    #{pr.rank}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: colors.text, marginTop: 2 }}>
                    ${Number(pr.amount).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Standings */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
            <span style={{
              width: 34, height: 34,
              border: `3px solid ${colors.accentDark}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'aga-spin 0.7s linear infinite',
            }} />
          </div>
        ) : error ? (
          <EmptyState icon="exclamation-circle" title="Couldn't load the leaderboard" body="Tap refresh to try again." />
        ) : !current || current.entries.length === 0 ? (
          <EmptyState icon="trophy" title="No one's on the board yet" body="Refer friends to claim the top spot." />
        ) : (
          <>
            <Podium entries={current.entries.slice(0, 3)} me={me} colors={colors} />

            {current.entries.length > 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {current.entries.slice(3).map(e => {
                  const isMe = me?.rank === e.rank && me?.qualifiedCount === e.qualifiedCount;
                  return (
                    <div
                      key={e.userId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        borderRadius: 14, padding: 12,
                        backgroundColor: isMe ? colors.accentSoft : colors.card,
                        border: `${isMe ? 2 : 1}px solid ${isMe ? colors.accentDark : colors.cardBorder}`,
                      }}
                    >
                      <div style={{
                        width: 30, height: 30, borderRadius: 9,
                        backgroundColor: colors.accentSoft,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 900, color: colors.accentText }}>{e.rank}</div>
                      </div>
                      <div style={{
                        width: 38, height: 38, borderRadius: 19,
                        backgroundColor: colors.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: '#020617' }}>{initials(e)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 800, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {displayName(e)}{isMe ? ' · You' : ''}
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                          {e.qualifiedCount} {e.qualifiedCount === 1 ? 'referral' : 'referrals'}
                        </div>
                      </div>
                      {e.prizeAmount && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Icon name="trophy" size={11} color="#fbbf24" />
                          <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>
                            ${Number(e.prizeAmount).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Podium({
  entries, me, colors,
}: {
  entries: Entry[];
  me: Standings['me'];
  colors: ThemeColors;
}) {
  if (entries.length === 0) return null;
  const byRank = (r: number) => entries.find(e => e.rank === r) ?? null;
  // Visual order: 2nd (left), 1st (center, elevated), 3rd (right).
  const order: { rank: number; height: number; size: number; raise: number }[] = [
    { rank: 2, height: 96, size: 58, raise: 22 },
    { rank: 1, height: 124, size: 72, raise: 0 },
    { rank: 3, height: 84, size: 54, raise: 34 },
  ];

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      gap: 8, marginTop: 6, marginBottom: 22,
    }}>
      {order.map(({ rank, height, size, raise }) => {
        const e = byRank(rank);
        if (!e) return <div key={rank} style={{ flex: 1 }} />;
        const medal = MEDAL_COLOR[rank];
        const isMe = me?.rank === e.rank && me?.qualifiedCount === e.qualifiedCount;
        const isFirst = rank === 1;
        return (
          <div key={rank} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: raise }}>
            {isFirst && (
              <div style={{ marginBottom: 2 }}>
                <Icon name="crown" size={20} color={medal} />
              </div>
            )}
            <div style={{
              position: 'relative',
              width: size + 8, height: size + 8, borderRadius: (size + 8) / 2,
              border: `2.5px solid ${medal}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 6,
            }}>
              <div style={{
                width: size, height: size, borderRadius: size / 2,
                backgroundColor: colors.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ color: '#020617', fontWeight: 900, fontSize: isFirst ? 24 : 18 }}>{initials(e)}</div>
              </div>
              <div style={{
                position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)',
                minWidth: 20, height: 20, borderRadius: 10,
                padding: '0 4px', boxSizing: 'border-box',
                border: `2px solid ${colors.bg}`, backgroundColor: medal,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ color: '#020617', fontSize: 11, fontWeight: 900 }}>{rank}</div>
              </div>
            </div>

            {isFirst && (
              <div style={{
                padding: '3px 10px', borderRadius: 999, backgroundColor: medal, marginBottom: 4,
              }}>
                <div style={{
                  color: '#020617', fontSize: 10, fontWeight: 900,
                  letterSpacing: 0.6, textTransform: 'uppercase',
                }}>
                  Winner
                </div>
              </div>
            )}

            <div style={{
              fontSize: 12.5, fontWeight: 800, marginBottom: 6, textAlign: 'center',
              maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: isMe ? colors.accentText : colors.text,
            }}>
              {displayName(e)}
            </div>

            <div style={{
              alignSelf: 'stretch', height, borderRadius: 14,
              border: `1px solid ${medal}`, backgroundColor: colors.card,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, padding: '0 6px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="coins" size={12} color={medal} />
                <div style={{ fontSize: 18, fontWeight: 900, color: colors.text }}>{e.qualifiedCount}</div>
              </div>
              {e.prizeAmount && (
                <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>
                  ${Number(e.prizeAmount).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
